export interface SupabaseConfigInfo {
  url: string | null;
  anonKey: string | null;
  host: string | null;
  runtimeProvider: "vm" | "supabase";
  functionsBaseUrl: string | null;
  functionsBaseHost: string | null;
  functionsBaseSource: string | null;
  functionsHealthCheckUrl: string | null;
  isFunctionsHostLocal: boolean;
  keyLength: number;
  maskedKey: string | null;
  keyType: "anon" | "publishable" | "unknown" | "missing";
  keySource: string | null;
  errors: string[];
  warnings: string[];
}

const isPublishableKey = (key: string) => key.startsWith("sb_publishable_");
const LOCAL_FUNCTION_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

const maskKey = (key: string) => {
  if (key.length <= 12) return `${key.slice(0, 2)}…${key.slice(-2)}`;
  return `${key.slice(0, 6)}…${key.slice(-6)}`;
};

export const getSupabaseConfigInfo = (): SupabaseConfigInfo => {
  const url = (import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || "").trim() || null;
  const runtimeProviderRaw = (
    import.meta.env.VITE_MYTHIC_RUNTIME_PROVIDER
    || import.meta.env.NEXT_PUBLIC_MYTHIC_RUNTIME_PROVIDER
    || "vm"
  ).trim().toLowerCase();
  const runtimeProvider: "vm" | "supabase" = runtimeProviderRaw === "supabase" ? "supabase" : "vm";
  const rawAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "").trim() || null;
  const rawPublishableKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY || "").trim() || null;
  const configuredFunctionsBaseUrl = (
    import.meta.env.VITE_MYTHIC_FUNCTIONS_BASE_URL
    || import.meta.env.VITE_TAILSCALE_FUNCTIONS_BASE_URL
    || import.meta.env.NEXT_PUBLIC_MYTHIC_FUNCTIONS_BASE_URL
    || import.meta.env.NEXT_PUBLIC_TAILSCALE_FUNCTIONS_BASE_URL
    || ""
  ).trim() || null;
  const rawFunctionsBaseUrl = configuredFunctionsBaseUrl ?? (runtimeProvider === "supabase" ? url : null);
  // Keep the same precedence as the generated Supabase client: anon key first, then publishable key.
  const anonKey = rawAnonKey || rawPublishableKey;
  const errors: string[] = [];
  const warnings: string[] = [];
  let host: string | null = null;
  let functionsBaseUrl: string | null = rawFunctionsBaseUrl;
  let functionsBaseHost: string | null = null;
  let functionsHealthCheckUrl: string | null = null;

  if (!url) {
    errors.push("Missing VITE_SUPABASE_URL");
  } else {
    try {
      const parsed = new URL(url);
      host = parsed.host;
      if (!url.startsWith("https://")) {
        errors.push("VITE_SUPABASE_URL must start with https://");
      }
      if (!parsed.host.endsWith(".supabase.co")) {
        errors.push("VITE_SUPABASE_URL host must end with .supabase.co");
      }
    } catch {
      errors.push("VITE_SUPABASE_URL is not a valid URL");
    }
  }

  if (!functionsBaseUrl) {
    errors.push("Missing functions runtime base URL (VITE_MYTHIC_FUNCTIONS_BASE_URL or VITE_TAILSCALE_FUNCTIONS_BASE_URL)");
  } else {
    try {
      const parsed = new URL(functionsBaseUrl);
      functionsBaseHost = parsed.host;
      if (parsed.hostname.endsWith(".supabase.co") && runtimeProvider !== "supabase") {
        errors.push("Functions base URL must point to your VM runtime, not *.supabase.co");
      }
      functionsBaseUrl = functionsBaseUrl.replace(/\/+$/, "");
      const routeBase = functionsBaseUrl.endsWith("/functions/v1")
        ? functionsBaseUrl.slice(0, -"/functions/v1".length)
        : functionsBaseUrl;
      functionsHealthCheckUrl = `${routeBase}/healthz`;
      if (LOCAL_FUNCTION_HOSTS.has(parsed.hostname.toLowerCase()) && runtimeProvider !== "supabase") {
        warnings.push("Functions base URL points to localhost. Ensure mythic-api is running.");
      }
    } catch {
      errors.push("Functions base URL must be a valid absolute URL");
      functionsBaseUrl = configuredFunctionsBaseUrl;
    }
  }

  if (!anonKey) {
    errors.push("Missing Supabase public key (VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY)");
  } else if (!isPublishableKey(anonKey) && !anonKey.startsWith("eyJ")) {
    warnings.push("Supabase public key format is unexpected. Expected an anon JWT-like key or sb_publishable_* key.");
  }

  const keyType: SupabaseConfigInfo["keyType"] = !anonKey
    ? "missing"
    : anonKey.startsWith("eyJ")
      ? "anon"
      : anonKey.startsWith("sb_publishable_")
        ? "publishable"
        : "unknown";

  const keySource = rawAnonKey
    ? (import.meta.env.VITE_SUPABASE_ANON_KEY ? "VITE_SUPABASE_ANON_KEY" : "NEXT_PUBLIC_SUPABASE_ANON_KEY")
    : rawPublishableKey
      ? (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ? "VITE_SUPABASE_PUBLISHABLE_KEY" : "NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY")
      : null;
  const functionsBaseSource = import.meta.env.VITE_MYTHIC_FUNCTIONS_BASE_URL
    ? "VITE_MYTHIC_FUNCTIONS_BASE_URL"
    : import.meta.env.VITE_TAILSCALE_FUNCTIONS_BASE_URL
      ? "VITE_TAILSCALE_FUNCTIONS_BASE_URL"
      : import.meta.env.NEXT_PUBLIC_MYTHIC_FUNCTIONS_BASE_URL
        ? "NEXT_PUBLIC_MYTHIC_FUNCTIONS_BASE_URL"
        : import.meta.env.NEXT_PUBLIC_TAILSCALE_FUNCTIONS_BASE_URL
          ? "NEXT_PUBLIC_TAILSCALE_FUNCTIONS_BASE_URL"
          : runtimeProvider === "supabase"
            ? "VITE_SUPABASE_URL (provider=supabase)"
            : null;
  const functionsHost = functionsBaseHost ?? null;
  const isFunctionsHostLocal = functionsHost
    ? LOCAL_FUNCTION_HOSTS.has(functionsHost.split(":")[0]?.toLowerCase() ?? "")
    : false;

  return {
    url,
    anonKey,
    host,
    runtimeProvider,
    functionsBaseUrl,
    functionsBaseHost,
    functionsBaseSource,
    functionsHealthCheckUrl,
    isFunctionsHostLocal,
    keyLength: anonKey?.length ?? 0,
    maskedKey: anonKey ? maskKey(anonKey) : null,
    keyType,
    keySource,
    errors,
    warnings,
  };
};
